<script setup>
// ChatListHeader.vue
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BlurDropdown from 'dashboard/components-next/dropdown/BlurDropdown.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

const props = defineProps({
  pageTitle: {
    type: String,
    required: true,
  },
  hasAppliedFilters: {
    type: Boolean,
    required: true,
  },
  hasActiveFolders: {
    type: Boolean,
    required: true,
  },
  activeStatus: {
    type: String,
    required: true,
  },
  hasSelectedConversation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'toggleConversationLayout',
]);

const showMoreMenu = ref(false);
const showMobileMenu = ref(false);
const toggleNewConversation = ref(null);
const createNewContactDialogRef = ref(null);
const windowWidth = ref(window.innerWidth);

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { uiSettings } = useUISettings();

// Mobile detection (768px breakpoint)
const isMobile = computed(() => windowWidth.value <= 768);

// Update window width on resize
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const currentAccountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const globalConfig = useMapGetter('globalConfig/get');

const isSidebarOpen = computed(() => uiSettings.value.show_secondary_sidebar);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const hideFiltersForAgents = computed(() => {
  return (
    currentRole.value !== 'administrator' &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      'hide_filters_for_agent'
    )
  );
});

const openCreateNewContactDialog = async () => {
  await createNewContactDialogRef.value?.contactsFormRef.resetValidation();
  createNewContactDialogRef.value?.dialogRef.open();
};

const onCreate = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const toggleMoreMenu = () => {
  showMoreMenu.value = !showMoreMenu.value;
};

const closeMoreMenu = () => {
  showMoreMenu.value = false;
};

const toggleSidemenu = () => {
  emitter.emit(BUS_EVENTS.TOGGLE_SIDEMENU);
};

// Mobile menu functions
const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
};

const closeMobileMenu = () => {
  showMobileMenu.value = false;
};

const mobileMenuItems = computed(() => [
  { id: 'conversations', title: 'Conversas', icon: 'message-circle' },
  { id: 'catalog', title: 'Catálogo', icon: 'shopping-bag' },
  { id: 'appointments', title: 'Agendamentos', icon: 'calendar' },
  ...(currentRole.value === 'administrator'
    ? [{ id: 'agentis', title: 'Agentes IA', icon: 'bot' }]
    : []),
  { id: 'bulk-message', title: 'Mensagens em Massa', icon: 'mail' },
  { id: 'kanban', title: 'Kanban', icon: 'align-start-horizontal' },
  { id: 'contacts', title: 'Contatos', icon: 'users' },
  { id: 'reports', title: 'Relatórios', icon: 'bar-chart-2' },
  { id: 'settings', title: 'Configurações', icon: 'settings' },
]);

const handleMobileMenuSelect = (item) => {
  closeMobileMenu();

  const routeMap = {
    'conversations': 'home',
    'catalog': 'products',
    'appointments': 'appointments_calendar',
    'agentis': 'agentis_list',
    'bulk-message': 'bulkMessage_new',
    'kanban': 'kanban_board',
    'contacts': 'contacts_dashboard_index',
    'reports': 'account_overview_reports',
    'settings': 'settings_inbox_list',
  };

  const routeName = routeMap[item.id];
  if (routeName) {
    router.push({ name: routeName, params: { accountId: currentAccountId.value } });
  }
};

const moreMenuItems = computed(() => {
  const items = [
    {
      id: 'filters',
      title: 'Filtros avançados',
      icon: 'list-filter',
      showChevron: true,
      badge: hasAppliedFiltersOrActiveFolders.value
    },
    { id: 'new-conversation', title: 'Nova conversa', icon: 'message-circle' },
    { id: 'new-contact', title: 'Novo contato', icon: 'user-round' },
  ];

  // Add toggle layout option only if a conversation is selected
  if (props.hasSelectedConversation) {
    items.push({
      id: 'toggle-layout',
      title: 'Esconder conversas',
      icon: 'arrow-left-to-line'
    });
  }

  // Add delete folder option if there's an active folder
  if (props.hasActiveFolders) {
    items.push({
      id: 'delete-folder',
      title: 'Excluir filtro salvo',
      icon: 'trash-2',
      destructive: true
    });
  }

  // Remove filters option if agent has restriction
  if (hideFiltersForAgents.value) {
    return items.filter(item => item.id !== 'filters');
  }
  return items;
});

const handleMenuSelect = (item) => {
  closeMoreMenu();

  // Route to appropriate action based on item.id
  if (item.id === 'filters') {
    emit('filtersModal');
  } else if (item.id === 'toggle-layout') {
    emit('toggleConversationLayout');
  } else if (item.id === 'delete-folder') {
    emit('deleteFolders');
  } else if (item.id === 'new-conversation') {
    if (toggleNewConversation.value) {
      toggleNewConversation.value();
    }
  } else if (item.id === 'new-contact') {
    openCreateNewContactDialog();
  }
};
</script>

<template>
  <div
    class="flex items-center gap-2 px-3 pb-2 pt-3"
  >
    <!-- <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-lg font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div> -->
    <div class="flex items-center justify-between w-full">
      <div class="flex flex-row items-center gap-2">
        <!-- Mobile Menu Button (visible only on mobile) -->
        <div v-if="isMobile" class="relative md:hidden">
          <NextButton
            icon="i-lucide-menu"
            size="sm"
            color="slate"
            variant="ghost"
            class="!p-0 !w-8 !h-8 !min-w-8 !min-h-8 !rounded-full grid place-items-center !leading-none !outline-0 border-none !text-n-slate-11 text-xl"
            @click="toggleMobileMenu"
          />
          <!-- Mobile Menu Dropdown -->
          <BlurDropdown
            v-if="showMobileMenu"
            :items="mobileMenuItems"
            :search="false"
            :clear="false"
            @select="handleMobileMenuSelect"
            @close="closeMobileMenu"
          />
        </div>
        <!-- Installation Logo -->
        <div class="flex items-center gap-2 select-none">
          <img
            :src="globalConfig.logoThumbnail"
            :alt="globalConfig.installationName"
            class="w-7 h-7 md:hidden"
          />
          <span class="text-nowrap text-n-slate-12 text-2xl font-medium leading-none">
            {{ globalConfig.installationName }}
          </span>
        </div>
      </div>

      <!-- Right side: Filters and More menu -->
      <div class="flex items-center gap-2">
        <!-- DESABILITADO: Menu sempre aberto -->
        <!-- <NextButton
          v-tooltip.bottom="'Barra lateral'"
          icon="i-lucide-panel-left"
          size="sm"
          color="slate"
          variant="ghost"
          :class="[
            'hidden md:grid !p-0 !px-0 !py-0 !w-8 !h-8 !min-w-8 !min-h-8 !rounded-full place-items-center !leading-none !outline-0 border-none !text-n-slate-11 text-base',
            'sidebar-toggle-btn',
            { 'sidebar-closed': !isSidebarOpen }
          ]"
          @click="toggleSidemenu"
        /> -->
        <template v-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              v-tooltip.bottom="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-pin"
              size="sm"
              color="slate"
              class="!outline-0 !rounded-full aspect-square !text-base"
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-40 mt-2 ltr:left-1/2 ltr:-translate-x-1/2 rtl:right-1/2 rtl:translate-x-1/2"
            />
          </div>
          <NextButton
            v-tooltip.top-bottom="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-x"
            size="sm"
            color="ruby"
            variant="ghost"
            class="!outline-0 !rounded-full aspect-square !text-base"
            @click="emit('resetFilters')"
          />
        </template>


        <ConversationBasicFilter
          v-else-if="!hideFiltersForAgents && !hasAppliedFiltersOrActiveFolders"
          @change-filter="onBasicFilterChange"
        />

        <div class="relative">
          <div class="relative inline-flex">
            <NextButton
              v-tooltip.top="'Mais opções'"
              icon="i-lucide-more-vertical"
              size="sm"
              color="slate"
              variant="ghost"
              class="!outline-0 !rounded-full aspect-square flex-shrink-0 !text-base !text-n-slate-11"
              @click="toggleMoreMenu"
            />
            <!-- Badge indicator on button -->
            <span
              v-if="hasAppliedFiltersOrActiveFolders"
              class="absolute top-0 right-0 w-2 h-2 bg-n-brand rounded-full"
            ></span>
          </div>

          <!-- More Menu Dropdown -->
          <BlurDropdown
            v-if="showMoreMenu"
            :items="moreMenuItems"
            :search="false"
            :clear="false"
            @select="handleMenuSelect"
            @close="closeMoreMenu"
          />

          <!-- Teleport target for conversation filter -->
          <div id="conversationFilterTeleportTarget" class="absolute z-40" />
        </div>
      </div>
    </div>

    <!-- New Conversation Modal -->
    <ComposeConversation align-position="center">
      <template #trigger="{ toggle }">
        <!-- Capture toggle function for programmatic control -->
        {{ (toggleNewConversation = toggle) && '' }}
      </template>
    </ComposeConversation>

    <!-- New Contact Dialog -->
    <CreateNewContactDialog ref="createNewContactDialogRef" @create="onCreate" />
  </div>
</template>

<style scoped lang="scss">
.sidemenu-icon-custom {
  @apply relative ltr:-ml-0 flex-shrink-0 !rounded-full hover:!bg-n-alpha-2 !outline-0;
  padding: 1rem !important;
}

// Smooth horizontal flip animation for sidebar toggle
.sidebar-toggle-btn {
  transition: transform 0.3s ease-in-out;

  &.sidebar-closed {
    transform: scaleX(-1);
  }
}
</style>
